import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Quote, Star, Loader2, Check, Send } from "lucide-react";

const FEATURED = [
  {
    name: "Jennette B.",
    role: "Parent",
    quote: "LP Foundation helped my son gain confidence and focus. He's excited about learning again.",
  },
];

const VIDEO_TESTIMONIALS = [
  { src: "/videos/gg_web.mp4", poster: "/videos/gg_poster.jpg", name: "Kara", role: "LP Foundation Family" },
  { src: "/videos/fred_web.mp4", poster: "/videos/fred_poster.jpg", name: "The Middlebrooks", role: "LP Foundation Family" },
  { src: "/videos/impact1_web.mp4", poster: "/videos/impact1_poster.jpg", name: "Richard", role: "LP Foundation Family" },
  { src: "/videos/thomas_web.mp4", poster: "/videos/thomas_poster.jpg", name: "Thomas White", role: "Henry County Recreation & Parks" },
];

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:ring-2 focus:ring-accent/40 focus:border-accent";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function TestimonialCard({ t }) {
  return (
    <div className="rounded-3xl border border-slate-100 bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
      <div className="flex gap-1 text-accent">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className="h-4 w-4 fill-current" />
        ))}
      </div>
      <Quote className="mt-4 h-8 w-8 text-slate-200" />
      <blockquote className="mt-2 text-lg text-slate-700 leading-relaxed">"{t.quote}"</blockquote>
      <div className="mt-5 flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-orange-50 font-display font-bold text-accent">
          {t.name?.[0]?.toUpperCase() || "L"}
        </span>
        <div>
          <p className="font-semibold text-brandnavy">{t.name}</p>
          {t.role && <p className="text-sm text-slate-500">{t.role}</p>}
        </div>
      </div>
    </div>
  );
}

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", role: "Parent", quote: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/testimonials", form);
      setDone(true);
      toast.success("Thank you for sharing your story!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const all = [...FEATURED, ...items];

  return (
    <div data-testid="testimonials-page">
      <section className="bg-brandnavy">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Testimonials</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight text-white max-w-3xl">
            Real stories from our community.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            Hear from the parents, students, and partners who've experienced the LP Foundation difference firsthand.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-20">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Video Stories</span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">
            Hear it in their words.
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Watch families and students share what LP Foundation means to them.
          </p>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          {VIDEO_TESTIMONIALS.map((v, i) => (
            <div
              key={i}
              data-testid={`video-testimonial-${i}`}
              className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              <video
                controls
                preload="metadata"
                poster={v.poster}
                className="w-full aspect-video bg-black"
                data-testid={`video-player-${i}`}
              >
                <source src={v.src} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="p-5">
                <p className="font-display font-bold text-brandnavy">{v.name}</p>
                {v.role && <p className="text-sm text-slate-500">{v.role}</p>}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {all.map((t, i) => (
            <div key={i} data-testid={`testimonial-card-${i}`}>
              <TestimonialCard t={t} />
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-2xl px-5 sm:px-8">
          <div className="text-center">
            <h2 className="font-display text-3xl font-black text-brandnavy">Share your story</h2>
            <p className="mt-3 text-slate-600">
              Has LP Foundation made a difference for your family? We'd love to hear about it. Submitted stories are reviewed
              before being published.
            </p>
          </div>
          <div className="mt-10 rounded-3xl border border-slate-100 bg-white p-6 sm:p-8 shadow-lg">
            {done ? (
              <div data-testid="testimonial-success" className="py-10 text-center">
                <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-green-100 text-green-600">
                  <Check className="h-8 w-8" />
                </span>
                <h3 className="mt-5 font-display text-2xl font-black text-brandnavy">Thank you!</h3>
                <p className="mt-2 text-slate-600">Your story has been submitted for review.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="testimonial-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Your Name *"><input data-testid="testimonial-name" required value={form.name} onChange={update("name")} className={inputCls} /></Field>
                  <Field label="You are a... *">
                    <select data-testid="testimonial-role" value={form.role} onChange={update("role")} className={inputCls}>
                      {["Parent", "Student", "Partner", "Volunteer", "Community Member"].map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Email (optional)"><input data-testid="testimonial-email" type="email" value={form.email} onChange={update("email")} className={inputCls} /></Field>
                <Field label="Your Story *"><textarea data-testid="testimonial-quote" required rows={4} value={form.quote} onChange={update("quote")} className={inputCls} /></Field>
                <button data-testid="testimonial-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent px-6 py-4 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-orange-400 disabled:opacity-60">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Submit My Story
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
